<script setup lang="ts">
import { mdiCalendar, mdiPlusCircle } from "@mdi/js";
import type { components } from "~/api_types";
import PreviewIcon from "~/components/PreviewIcon.vue";
import { useCalendar } from "~/composables/calendar";
import { entityPath, isEntityType } from "~/utils/entityPath";

type LocationDetailsResponse = components["schemas"]["LocationDetailsResponse"];
type CalendarLocationResponse = components["schemas"]["CalendarLocationResponse"];

defineProps<{
  readonly data: Map<string, CalendarLocationResponse>;
}>();
const { t } = useI18n({ useScope: "local" });
const runtimeConfig = useRuntimeConfig();
const calendar = useCalendar();

async function addLocation() {
  let selectedLocation = "";
  while (!selectedLocation) {
    selectedLocation = window.prompt(t("prompt.initial")) || "";
    try {
      // biome-ignore lint/performance/noAwaitInLoops: sequential interactive retry loop; each iteration re-prompts on the prior result.
      const result = await fetch(
        `${runtimeConfig.public.apiURL}/api/locations/${selectedLocation}`
      );
      if (!result.ok) {
        const userWantsToRetry = window.confirm(t("prompt.error_not_ok", [selectedLocation]));
        if (!userWantsToRetry) return;
        selectedLocation = "";
        continue;
      }
      const res = (await result.json()) as LocationDetailsResponse;
      if (!res.props.calendar_url) {
        const userWantsToRetry = window.confirm(t("prompt.error_not_calendar", [selectedLocation]));
        if (!userWantsToRetry) return;
        selectedLocation = "";
        continue;
      }
    } catch (e) {
      window.alert(`Failed because ${e}`);
      selectedLocation = "";
      continue;
    }
    if (calendar.value.find((k) => k === selectedLocation)) {
      const userWantsToRetry = window.confirm(t("prompt.error_already_exists", [selectedLocation]));
      if (!userWantsToRetry) return;
      selectedLocation = "";
    }
  }
  calendar.value = [...calendar.value, selectedLocation];
  // todo: debug why this is not syncinc apropriately, quite a crude hack
  setTimeout(() => location.reload(), 500);
}
</script>

<template>
  <ul v-if="calendar.length" class="mb-6 flex gap-2 overflow-x-auto">
    <li
      v-for="[key, location] in data.entries()"
      :key="key"
      class="flex min-w-64 gap-1 rounded-md px-2 md:gap-3 md:px-4"
    >
      <PreviewIcon
        :item="{ kind: 'location', type: isEntityType(location.type) ? location.type : undefined }"
      />
      <div class="text-zinc-600 dark:text-zinc-300 flex flex-col gap-1">
        <NuxtLinkLocale
          v-if="isEntityType(location.type)"
          class="line-clamp-1 hover:underline"
          :to="entityPath(key, location.type)"
          >{{ location.name }}</NuxtLinkLocale
        >
        <span v-else class="line-clamp-1">{{ location.name }}</span>
        <small>
          {{ location.type_common_name }}
        </small>
        <small>
          <Btn :to="location.calendar_url" variant="link" size="text-xs font-semibold rounded-md">
            <MdiIcon :path="mdiCalendar" :size="16" class="mb-0.5" />
            {{ t("view_in_tumonline") }}
          </Btn>
        </small>
      </div>
    </li>
    <li class="flex">
      <button
        type="button"
        class="focusable group bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700 flex min-w-14 origin-center place-items-center content-center items-center justify-center justify-items-center justify-self-center rounded-md border object-center align-middle hover:bg-blue-100 dark:hover:bg-blue-800"
        :title="t('add_location')"
        :aria-label="t('add_location')"
        @click="addLocation"
      >
        <MdiIcon :path="mdiPlusCircle" :size="20" class="group-hover:text-black dark:group-hover:text-white" />
      </button>
    </li>
  </ul>
</template>

<i18n lang="yaml">
de:
  add_location: Zusätzliche Location zum Kalender hinzufügen
  view_in_tumonline: in TUMonline ansehen
  prompt:
    initial: |-
      Welcher Kalender soll hinzugefügt werden?
      Bitte wähle nur den letzten Teil der Url eines Raumes mit einem Kalender.

      Beispiel:
      https://nav.tum.de/room/5502.01.250 hat die ID 5502.01.250
    error_not_ok: |-
      Der Ort '{0}' existiert nicht.
      Wenn er existieren würde, gäbe es https://nav.tum.de/view/{0}.

      Möchtest du es mit einer anderen ID erneut versuchen?
    error_not_calendar: |-
      Der Ort hat keinen Kalender.
      Möchtest du es mit einer anderen ID erneut versuchen?
    error_already_exists: |-
      Der Ort ist bereits im Kalender eingetragen.
      Möchtest du es mit einer anderen ID erneut versuchen?
en:
  add_location: add additional location to the calendar
  view_in_tumonline: View in TUMonline
  prompt:
    initial: |-
      Which calendar should be added?
      Please select just the last part of the url of a room with a calendar.

      Example:
      https://nav.tum.de/room/5502.01.250 has the id 5502.01.250
    error_not_ok: |-
      Location '{0}' does not exist.
      If it were to exist https://nav.tum.de/view/{0} would exist.
      Want to retry with a different id?
    error_not_calendar: |-
      Location does not have a calendar.
      Want to retry with a different id?
    error_already_exists: |-
      Location is already in the calendar.
      Want to retry with a different id?
</i18n>
